import { Plus } from 'lucide-react';
import { BaseForecastModelWithAvailability, ForecastModel } from '@app/common/static-data/models';

interface ModelQuickSelectorProps {
  availableModels: BaseForecastModelWithAvailability[];
  favoriteModels: Set<string>;
  selectedModels: ForecastModel[];
  onModelToggle: (modelId: ForecastModel) => void;
  onOpenPanel: () => void;
  loading?: boolean;
}

export function ModelQuickSelector({
  availableModels,
  favoriteModels,
  selectedModels,
  onModelToggle,
  onOpenPanel,
  loading = false,
}: ModelQuickSelectorProps) {
  // Get favorite models that are also available
  const favoriteModelsList = availableModels.filter((model) => favoriteModels.has(model.id));

  const handleModelToggle = (modelId: string) => {
    onModelToggle(modelId as ForecastModel);
  };

  // If no favorites, show "Select favorite models" message
  if (favoriteModelsList.length === 0) {
    return (
      <button
        onClick={onOpenPanel}
        className="px-2 py-1 rounded text-xs font-medium transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400"
        disabled={loading}
      >
        Select favorite models
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 min-w-0 w-full sm:w-auto">
      <div
        className="flex items-center gap-1 overflow-x-auto scrollbar-hide min-w-0 flex-1 sm:flex-initial"
        style={{ maxHeight: '3rem' }}
      >
        {favoriteModelsList.map((model) => (
          <button
            key={model.id}
            onClick={() => handleModelToggle(model.id)}
            className={`
              px-2 py-1 rounded text-xs font-medium transition-all duration-200 flex-shrink-0 whitespace-nowrap
              ${
                selectedModels.includes(model.id as ForecastModel)
                  ? 'text-pink-500 dark:text-pink-400'
                  : 'text-gray-600 dark:text-gray-300'
              }
            `}
            disabled={loading}
            title={model.label}
          >
            <span className="truncate max-w-16">{model.label}</span>
          </button>
        ))}
      </div>

      {/* Plus icon to add more models - always visible */}
      <div className="relative flex-shrink-0 ml-1">
        <button
          onClick={onOpenPanel}
          className="group px-1.5 py-1 rounded text-xs font-medium transition-all duration-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 relative"
          disabled={loading}
        >
          <Plus className="h-3 w-3" />

          {/* Tooltip */}
          <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 left-0 top-full mt-1 px-2 py-1 bg-black text-white text-xs rounded whitespace-nowrap z-50">
            Add more models
          </div>
        </button>
      </div>
    </div>
  );
}
